import React, { forwardRef, useCallback, useState } from "react";
import { cn } from "../../lib/utils.js";
import {
  FLOATING_SURFACE,
  GLASS_SURFACE,
  SELECTED,
} from "../../lib/surfaces.js";
import {
  cardColourClasses,
  cardStripe,
  type CardColourProps,
  type CardStripe,
  type CardTint,
} from "./card-colour.js";
import { CardSizeContext, type CardSize } from "./card-size.js";
import { CardStickyContext } from "./card-sticky.js";

/* ---------------- Card (compound family) ---------------- */

type ElementProps = React.HTMLAttributes<HTMLElement>;

export type { CardSize, CardStripe, CardTint };

/** Surface style: the card, a recessed well, or an empty dashed frame. */
export type CardVariant = "default" | "inset" | "dashed";

/** How far the card sits above the page. */
export type CardElevation = "flat" | "raised" | "floating";

/** Paint of a floating card: the workspace paint or glass over a canvas. */
export type CardSurface = "default" | "glass";

/** The element the card renders. */
export type CardElement = "div" | "section" | "article" | "button";

const VARIANT_CORNER: Record<CardVariant, string> = {
  default: "rounded-xl",
  inset: "rounded-lg",
  dashed: "rounded-xl",
};

const VARIANT_PAINT: Record<CardVariant, string> = {
  default: "border border-pb-border/80 bg-pb-card",
  inset: "border border-pb-border/70 bg-pb-card-elevated/60",
  dashed: "border border-dashed border-pb-border-strong bg-pb-card/30",
};

/* The floating paints live in lib/surfaces.ts, shared with the floating bars;
   they hold no radius, so the card keeps its own corner. */
const FLOATING_PAINT: Record<CardSurface, string> = {
  default: FLOATING_SURFACE,
  glass: GLASS_SURFACE,
};

/* A lifted, neutral card: never the SELECTED teal (a dragged card is not
   selected, decision 3) and never pb-accent (the region's one commit). */
const DRAGGING = "shadow-2xl border-pb-border-strong";

/* ModuleCard's and CanvasWidget's dimmed values. */
const DIMMED = "opacity-75 saturate-75";

/* The state changes animate and stop under reduced motion. The list also
   holds the properties of the lift and press grades, so an interactive card
   keeps its motion whichever transition wins. */
const STATE_MOTION =
  "transition-[border-color,box-shadow,opacity,filter,transform] duration-motion-state ease-motion-standard motion-reduce:transition-none";

/* A native button keeps the card's box: block, full width, start-aligned. */
const BUTTON_BOX = "block w-full text-start";

interface CardBaseProps extends ElementProps {
  /** The element to render. Default `div`. `button` is a native button with
   *  type="button", the focus ring and Enter/Space activation. A menu or any
   *  other control sits beside a button card, never inside it. */
  as?: CardElement;
  /** A chosen card: the teal glow on a teal edge (SELECTED). On
   *  `as="button"` it also sets aria-pressed. */
  selected?: boolean;
  /** A locked or out-of-play card: lower opacity and saturation. */
  dimmed?: boolean;
  /** A card that is being dragged: a large shadow and a neutral strong
   *  border. While it lasts it wins over `selected`. */
  dragging?: boolean;
  /** A clickable card: lifts on hover, dips on press, pointer cursor. A
   *  `selected` or `dragging` card does not lift (nor tint), so its look holds
   *  under the pointer. */
  interactive?: boolean;
  /** `inset` is a recessed well (10px corner); `dashed` is the empty-state
   *  frame. Neither carries the top hairline. Default `default`. */
  variant?: CardVariant;
  /** Padding of the CardHeader, CardBody and CardFooter inside this card:
   *  `sm` is compact, `md` (default) is today's. */
  size?: CardSize;
}

/* `surface` only means something on a floating card, so the type refuses it
   anywhere else. */
type CardElevationProps =
  | {
      /** `raised` adds a medium shadow; `floating` paints a floating surface
       *  in place of the variant's border and fill. Default `flat`. */
      elevation?: "flat" | "raised";
      surface?: never;
    }
  | {
      elevation: "floating";
      /** `default` is the workspace paint; `glass` is glass over a canvas. */
      surface?: CardSurface;
    };

export type CardProps = CardBaseProps & CardElevationProps & CardColourProps;

/* The ref is an HTMLElement because the element varies; an existing
   HTMLDivElement ref object or callback still type-checks against it. */
const CardRoot = forwardRef<HTMLElement, CardProps>(function Card(
  {
    className,
    children,
    as: Element = "div",
    selected,
    dimmed,
    dragging,
    interactive,
    tone,
    tint,
    stripe,
    variant = "default",
    size = "md",
    elevation = "flat",
    surface = "default",
    ...rest
  },
  ref,
) {
  const floating = elevation === "floating";
  // Count, not a flag: two sticky headers may mount and unmount independently.
  const [stickyHeaders, setStickyHeaders] = useState(0);
  const registerSticky = useCallback(() => {
    setStickyHeaders((count) => count + 1);
    return () => setStickyHeaders((count) => count - 1);
  }, []);
  const button = Element === "button";
  // A card that takes any state flag, even `false`, carries the state motion
  // so the change animates both ways. With no flag the classes are today's.
  const stateful =
    selected !== undefined || dimmed !== undefined || dragging !== undefined;
  // hover-lift and hover-tint paint a shadow and a border under `:hover`,
  // which outranks SELECTED and DRAGGING, so a card showing either takes no
  // hover grade (ModuleCard's rule). With no state the classes are today's.
  const hoverGrade = !selected && !dragging;
  const bar = cardStripe(tone, tint, stripe);
  return (
    <CardSizeContext.Provider value={size}>
      <CardStickyContext.Provider value={registerSticky}>
        <Element
          ref={ref as React.Ref<never>}
          {...(button ? { type: "button" as const } : {})}
          aria-pressed={button && selected !== undefined ? selected : undefined}
          className={cn(
            "overflow-hidden",
            VARIANT_CORNER[variant],
            floating ? FLOATING_PAINT[surface] : VARIANT_PAINT[variant],
            // pb-hairline is itself a box-shadow that the CSS declares after
            // Tailwind's shadows, so it would cancel shadow-md, the selected
            // glow and the drag shadow: only the flat default card at rest
            // carries it.
            variant === "default" &&
              elevation === "flat" &&
              !selected &&
              !dragging &&
              "pb-hairline",
            elevation === "raised" && "shadow-md",
            interactive && [hoverGrade && "hover-lift", "press cursor-pointer"],
            // Before the states, so SELECTED and DRAGGING keep their border.
            cardColourClasses(tone, tint, hoverGrade),
            // A start bar is absolute, so it spans the card's full height.
            bar?.placement === "start" && "relative",
            button && [BUTTON_BOX, "focus-ring"],
            stateful && STATE_MOTION,
            selected && SELECTED,
            dragging && DRAGGING,
            dimmed && DIMMED,
            // A sticky header needs the card to stop clipping (card-sticky.ts).
            // Corners and the tint stripe then no longer clip on this card.
            stickyHeaders > 0 && "overflow-visible",
            className,
          )}
          {...rest}
        >
          {bar ? <span aria-hidden className={bar.className} /> : null}
          {children}
        </Element>
      </CardStickyContext.Provider>
    </CardSizeContext.Provider>
  );
});

export const Card = CardRoot;
